/**
 * useWebPush — Vague 99.5 — Web Push subscription helper.
 * Lifecycle: register service worker → fetch VAPID public key → subscribe → POST to backend.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';

function urlB64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function useWebPush() {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const [status, setStatus] = useState('unknown'); // unknown | denied | granted | unsubscribed | subscribed
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) { setStatus('denied'); return; }
    if (Notification.permission === 'denied') { setStatus('denied'); return; }
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) { setStatus('unsubscribed'); return; }
      const sub = await reg.pushManager.getSubscription();
      setStatus(sub ? 'subscribed' : 'unsubscribed');
    } catch (e) {
      setStatus('unsubscribed');
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!supported) return false;
    setLoading(true);
    try {
      const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      if (perm !== 'granted') { setStatus('denied'); return false; }
      const reg = (await navigator.serviceWorker.getRegistration('/sw.js'))
        || (await navigator.serviceWorker.register('/sw.js'));
      const { data } = await api.get('/webpush/public-key');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      });
      const subJson = sub.toJSON();
      await api.post('/webpush/subscribe', {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
      });
      setStatus('subscribed');
      return true;
    } catch (e) {
      console.warn('[webpush] subscribe failed', e);
      return false;
    } finally { setLoading(false); }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const subJson = sub.toJSON();
        try { await api.post('/webpush/unsubscribe', { endpoint: subJson.endpoint, keys: subJson.keys }); } catch (e) { /* ignore */ }
        await sub.unsubscribe();
      }
      setStatus('unsubscribed');
    } finally { setLoading(false); }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await api.post('/webpush/test');
      return data?.sent || 0;
    } catch (e) {
      return 0;
    }
  }, []);

  return { supported, status, loading, subscribe, unsubscribe, sendTest, refresh };
}
